import React, { FC, useEffect, useState } from 'react';
import { Icons } from '@components/Icon';
import useTranslations from '@hooks/useTranslation';
import { handleDownloadClick } from '@utils/material.utils';
import moment from 'moment';
import {
    Benefit,
    BenefitIcon,
    BenefitLabel,
    Benefits,
    HarmonogramDay,
    HarmonogramDayDescription,
    HarmonogramDayTime,
    HarmonogramPartTitle,
    Material,
    MaterialIcon,
    MaterialLabel,
    Materials,
    Section,
    Title,
    Wrapper,
} from './TrainingBenefit.elements';

export interface TrainingBenefitProps {
    benefits: string;
    bonuses: string;
    harmonogram: {
        title: string;
        description: string;
        part: {
            description: string;
            from: Date;
            to: Date;
        }[];
    }[];
    instructions: string;
    instructionAttachments: {
        _key: string;
        name: string;
        filename: string;
    }[];
}

const ONLINE_BONUSES_PREFIX = 'o:';
const PRESENCE_BONUSES_PREFIX = 'p:';

const TrainingBenefitTemplate: FC<TrainingBenefitProps> = ({
    benefits,
    bonuses,
    harmonogram: defaultHarmonogram,
    instructions,
    instructionAttachments,
}) => {
    const { t } = useTranslations();

    const [harmonogram, setHarmonogram] = useState([]);

    useEffect(() => {
        if (defaultHarmonogram) {
            setHarmonogram(defaultHarmonogram);
        }
    }, []);

    const globalBonuses = bonuses
        ?.split('|')
        ?.filter((bonus) => !bonus?.includes(ONLINE_BONUSES_PREFIX) && !bonus?.includes(PRESENCE_BONUSES_PREFIX));
    const onlineBonuses = bonuses?.split('|')?.filter((bonus) => bonus?.includes(ONLINE_BONUSES_PREFIX));
    const presenceBonuses = bonuses?.split('|')?.filter((bonus) => bonus?.includes(PRESENCE_BONUSES_PREFIX));

    console.log('AA', instructionAttachments);
    return (
        <Wrapper>
            {!!benefits && benefits?.split('|')?.length > 0 && (
                <Section>
                    <Title>{t('training.benefits')}:</Title>
                    <Benefits>
                        {benefits?.split('|')?.map((benefit: string, index: number) => (
                            <Benefit key={index}>
                                <BenefitIcon name={Icons.CheckThin} />
                                <BenefitLabel>{benefit}</BenefitLabel>
                            </Benefit>
                        ))}
                    </Benefits>
                </Section>
            )}
            {!!bonuses && !!globalBonuses && globalBonuses?.length > 0 && (
                <Section>
                    <Title>{t('training.bonuses')}:</Title>
                    <Benefits>
                        {globalBonuses?.map((bonus: string, index: number) => (
                            <Benefit key={index}>
                                <BenefitIcon name={Icons.CheckThin} />
                                <BenefitLabel>{bonus}</BenefitLabel>
                            </Benefit>
                        ))}
                    </Benefits>
                </Section>
            )}
            {!!bonuses && !!onlineBonuses && onlineBonuses?.length > 0 && (
                <Section>
                    <Title>{t('training.bonuses.online')}:</Title>
                    <Benefits>
                        {onlineBonuses?.map((bonus: string, index: number) => (
                            <Benefit key={index}>
                                <BenefitIcon name={Icons.CheckThin} />
                                <BenefitLabel>{bonus?.replace(ONLINE_BONUSES_PREFIX, '')}</BenefitLabel>
                            </Benefit>
                        ))}
                    </Benefits>
                </Section>
            )}
            {!!bonuses && !!presenceBonuses && presenceBonuses?.length > 0 && (
                <Section>
                    <Title>{t('training.bonuses.presence')}:</Title>
                    <Benefits>
                        {presenceBonuses?.map((bonus: string, index: number) => (
                            <Benefit key={index}>
                                <BenefitIcon name={Icons.CheckThin} />
                                <BenefitLabel>{bonus?.replace(PRESENCE_BONUSES_PREFIX, '')}</BenefitLabel>
                            </Benefit>
                        ))}
                    </Benefits>
                </Section>
            )}
            {harmonogram?.length > 0 && (
                <Section>
                    <Title>{t('training.program')}:</Title>
                    {harmonogram?.map(
                        (
                            day: { title: string; part: { description: string; from: Date; to: Date }[] },
                            dayIndex: number
                        ) => (
                            <div key={dayIndex}>
                                {harmonogram?.length > 1 && <HarmonogramPartTitle>{day.title}</HarmonogramPartTitle>}
                                <div>
                                    {day.part?.map(
                                        (part: { description: string; from: Date; to: Date }, partIndex: number) => (
                                            <HarmonogramDay key={partIndex}>
                                                <HarmonogramDayTime>
                                                    {moment(part.from).local().format('HH:mm')} -{' '}
                                                    {moment(part.to).local().format('HH:mm')}
                                                </HarmonogramDayTime>
                                                <HarmonogramDayDescription
                                                    dangerouslySetInnerHTML={{ __html: part.description }}
                                                />
                                            </HarmonogramDay>
                                        )
                                    )}
                                </div>
                            </div>
                        )
                    )}
                </Section>
            )}
            {(instructions || instructionAttachments?.length > 0) && (
                <Section>
                    <Title>{t('training.instructions')}:</Title>
                    <Benefits dangerouslySetInnerHTML={{ __html: instructions }} />
                    <Materials>
                        {instructionAttachments
                            ?.filter((attachment) => attachment)
                            ?.map((attachment, key) => (
                                <Material
                                    onClick={() => handleDownloadClick(attachment._key, attachment.filename)}
                                    key={key}
                                >
                                    <MaterialIcon name={Icons.File} />
                                    <MaterialLabel>{attachment?.name}</MaterialLabel>
                                </Material>
                            ))}
                    </Materials>
                </Section>
            )}
        </Wrapper>
    );
};

export default TrainingBenefitTemplate;
